import { createContext, useContext } from 'react';

/**
 * OnboardingContext - Provides onboarding trigger functionality and state
 *
 * Used by AccountSettings and other components to trigger the onboarding wizard.
 * Also provides access to contextual tips, checklist state, and preferences.
 */
export const OnboardingContext = createContext({
  // Wizard controls
  triggerOnboarding: () => {},
  restartTour: async () => false,
  isOnboardingActive: false,
  currentStep: 0,
  setCurrentStep: () => {},

  // Progress persistence
  savedProgress: null,
  saveProgress: () => {},
  clearProgress: () => {},

  // Checklist
  checklistItems: [],
  checklistVisible: true,
  toggleChecklist: () => {},
  completeChecklistItem: () => {},
  isChecklistComplete: false,

  // Contextual tips
  activeTip: null,
  showTip: () => {},
  dismissTip: () => {},
  dismissedTips: [],

  // Preferences
  preferences: {
    showChecklist: true,
    showTips: true,
    locale: 'en',
  },
  updatePreferences: () => {},

  // Interactive highlights
  highlightElement: null,
  setHighlightElement: () => {},
  clearHighlight: () => {},

  // Analytics
  analytics: null,
});

/**
 * useOnboarding - Hook to access onboarding controls
 * @returns {Object} Onboarding context value
 */
export function useOnboarding() {
  return useContext(OnboardingContext);
}

/**
 * useContextualTip - Hook to show/hide contextual tips
 * @param {string} tipId - Unique tip identifier
 * @returns {Object} Tip controls
 */
export function useContextualTip(tipId) {
  const { activeTip, showTip, dismissTip, dismissedTips, preferences } = useOnboarding();

  const isVisible = activeTip === tipId && preferences.showTips;
  const isDismissed = dismissedTips.includes(tipId);

  return {
    isVisible,
    isDismissed,
    show: () => !isDismissed && showTip(tipId),
    dismiss: (permanent = false) => dismissTip(tipId, permanent),
  };
}

/**
 * useOnboardingChecklist - Hook to access checklist state
 * @returns {Object} Checklist controls
 */
export function useOnboardingChecklist() {
  const {
    checklistItems,
    checklistVisible,
    toggleChecklist,
    completeChecklistItem,
    isChecklistComplete,
    preferences,
  } = useOnboarding();

  return {
    items: checklistItems,
    isVisible: checklistVisible && preferences.showChecklist,
    isComplete: isChecklistComplete,
    toggle: toggleChecklist,
    complete: completeChecklistItem,
  };
}

export default OnboardingContext;
